import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ConfigType,
  FilterDefinition,
  SelectOption,
} from '@pflegenavi/web-components';
import { useReceiptTypes } from '@pflegenavi/frontend/api-nursing-home';
import { useTranslateAndSortReceiptTypes } from '@pflegenavi/shared/frontend';
import type {
  ReceiptBatchFilterKey,
  ReceiptBatchFiltersState,
} from './useReceiptBatchFilters';

/**
 * The filter pills for the receipt batch table. Each item reads and writes the
 * URL-backed `filtersState`. Without `showServiceProviderFilters` the service
 * provider and receipt type pills are left out, for a table already pinned to
 * one service provider.
 *
 * @example
 * const filters = useReceiptBatchFilterDefinitions(filtersState, true);
 * makeConfig({ filters, columns: [...] })
 */
export function useReceiptBatchFilterDefinitions(
  filtersState: ReceiptBatchFiltersState,
  showServiceProviderFilters: boolean
): NonNullable<ConfigType<never, never, ReceiptBatchFilterKey>['filters']> {
  const { t } = useTranslation();
  const { values, set } = filtersState;

  const { data: receiptTypes } = useReceiptTypes({
    enabled: showServiceProviderFilters,
  });
  const { translateAndSortReceiptTypes } = useTranslateAndSortReceiptTypes();
  const receiptTypeOptions = useMemo<SelectOption[]>(
    () =>
      translateAndSortReceiptTypes({ transactionTypes: receiptTypes }).map(
        ({ value, label }) => ({ id: value, label })
      ),
    [receiptTypes, translateAndSortReceiptTypes]
  );

  return useMemo(() => {
    const serviceProviderFilters: Array<
      FilterDefinition<ReceiptBatchFilterKey>
    > = showServiceProviderFilters
      ? [
          {
            key: 'serviceProvider',
            type: 'text',
            label: t('receipts.batch.overview-table.columns.service-provider'),
            value: values.serviceProvider,
            onChange: (value) => set('serviceProvider', value),
          },
          {
            key: 'receiptType',
            type: 'select',
            label: t('common.receipt-type'),
            options: receiptTypeOptions,
            value: values.receiptType,
            onChange: (value) => set('receiptType', value),
          },
        ]
      : [];

    return {
      variant: 'pills',
      items: [
        ...serviceProviderFilters,
        {
          key: 'receiptDate',
          type: 'dateRange',
          label: t('receipts.batch.overview-table.filters.receiptDate'),
          range: values.receiptDate,
          onChange: (range) => set('receiptDate', range),
        },
        {
          key: 'submissionDate',
          type: 'dateRange',
          label: t('receipts.batch.overview-table.filters.submissionDate'),
          range: values.submissionDate,
          onChange: (range) => set('submissionDate', range),
        },
      ],
    };
  }, [t, values, set, receiptTypeOptions, showServiceProviderFilters]);
}
